import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../../utils/api';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { Send, Sparkles, Instagram, Facebook, Loader2, CheckCircle, AlertCircle, Film, Image, Circle, Clock, CalendarClock, Save } from 'lucide-react';
import { BRAND_VOICES, CONTENT_TYPES, REEL_HOOKS, CTA_PATTERNS } from '../../../constants/captions';

const STATUS_LABELS = {
  idle: 'Ready',
  generating: 'Generating caption...',
  publishing: 'Publishing now...',
  scheduling: 'Scheduling...',
  success: 'Done!',
  error: 'Failed',
};

/** Minimum datetime = now + 5 minutes, formatted for datetime-local input */
const getMinDateTime = () => {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  return d.toISOString().slice(0, 16);
};

const getDefaultDateTime = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000); // 1 hour from now
  return d.toISOString().slice(0, 16);
};

export const PublishDialog = ({ isOpen, onClose, publicVideoUrl, compositionName, propertyData, initialMode = 'now' }) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [contentType, setContentType] = useState('reel');
  const [brandVoice, setBrandVoice] = useState('minimalist');
  const [caption, setCaption] = useState('');
  const [publishMode, setPublishMode] = useState('now'); // 'now' or 'schedule'
  const [scheduleDate, setScheduleDate] = useState(getDefaultDateTime());
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
      setStatus('idle');
      setError('');
      setPublishMode(initialMode);
      setScheduleDate(getDefaultDateTime());
      // Auto-generate caption if property data available and caption is empty
      if (propertyData && (propertyData.address || propertyData.price) && !caption) {
        handleGenerateCaption();
      }
    }
  }, [isOpen]);

  // Stop polling if the dialog unmounts.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleClose = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    onClose();
  };

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await apiClient.get('/social/accounts');
      const accts = res.data.accounts || [];
      setAccounts(accts);
      // Pre-select ALL connected accounts so users publish to FB + IG together by default.
      setSelectedAccountIds(accts.map(a => a.account_id));
    } catch {
      toast.error('Failed to load social accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const toggleAccount = (accountId) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    );
  };

  const handleGenerateCaption = async () => {
    setGeneratingCaption(true);
    try {
      const res = await apiClient.post('/social/caption/generate', {
        content_type: contentType,
        brand_voice: brandVoice,
        property_data: propertyData || {},
      });
      setCaption(res.data.caption || '');
      toast.success('Caption generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate caption');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const handlePublishNow = async () => {
    if (selectedAccountIds.length === 0) { toast.error('Select at least one account'); return; }
    if (!publicVideoUrl && contentType === 'reel') { toast.error('No video URL — render the reel first'); return; }

    setStatus('publishing');
    setError('');
    setProgressMsg('Starting publish…');
    try {
      const res = await apiClient.post('/social/publish-async', {
        account_ids: selectedAccountIds,
        content_type: contentType,
        caption,
        media_url: publicVideoUrl,
        generate_caption: !caption,
        brand_voice: brandVoice,
        property_data: propertyData || {},
      });
      pollPublishJob(res.data.job_id);
    } catch (err) {
      setStatus('error');
      const msg = err.response?.data?.detail || err.message || 'Publishing failed';
      setError(msg);
      toast.error(msg);
    }
  };

  const finalizePublish = (job) => {
    const okCount = job.success_count ?? (job.accounts || []).filter(a => a.ok).length;
    const total = job.total || selectedAccountIds.length;
    if (okCount >= total) {
      setStatus('success');
      toast.success(`Published to ${okCount} account${okCount > 1 ? 's' : ''}!`);
    } else if (okCount > 0) {
      setStatus('success');
      const failed = (job.accounts || []).filter(a => !a.ok);
      toast.warning(`Published to ${okCount} of ${total}. ${failed.length} failed.`);
      setError(failed.map(f => f.error).filter(Boolean).join(' | '));
    } else {
      setStatus('error');
      const msg = (job.accounts || []).map(a => a.error).filter(Boolean)[0] || 'Publishing failed';
      setError(msg);
      toast.error(msg);
    }
  };

  const pollPublishJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 3;
      try {
        const res = await apiClient.get(`/social/publish-status/${jobId}`);
        const job = res.data;
        const done = (job.accounts || []).filter(a => a.status === 'success' || a.status === 'failed').length;
        setProgressMsg(`Publishing… ${done}/${job.total} done${elapsed > 12 ? ' — Reels can take up to a minute' : ''}`);
        if (job.status === 'complete') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProgressMsg('');
          finalizePublish(job);
          return;
        }
      } catch {
        // transient error — keep polling until the timeout below
      }
      if (elapsed > 480 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setProgressMsg('');
        setStatus('error');
        setError('Timed out waiting for the publish to finish. It may still complete — check your social accounts.');
      }
    }, 3000);
  };

  const handleSchedule = async () => {
    if (selectedAccountIds.length === 0) { toast.error('Select at least one account'); return; }
    if (!scheduleDate) { toast.error('Select a date and time'); return; }

    const scheduledAt = new Date(scheduleDate).toISOString();
    if (new Date(scheduledAt) <= new Date()) {
      toast.error('Scheduled time must be in the future');
      return;
    }

    setStatus('scheduling');
    setError('');
    const results = [];
    for (const accountId of selectedAccountIds) {
      try {
        const res = await apiClient.post('/social/schedule', {
          account_id: accountId,
          content_type: contentType,
          caption,
          media_url: publicVideoUrl || '',
          scheduled_at: scheduledAt,
          brand_voice: brandVoice,
          property_data: propertyData || {},
          generate_caption: !caption,
        });
        results.push({ accountId, ok: !!res.data.success });
      } catch (err) {
        results.push({ accountId, ok: false, error: err.response?.data?.detail || err.message || 'Scheduling failed' });
      }
    }
    const okCount = results.filter(r => r.ok).length;
    if (okCount === selectedAccountIds.length) {
      setStatus('success');
      const dt = new Date(scheduledAt).toLocaleString();
      toast.success(`Scheduled ${okCount} account${okCount > 1 ? 's' : ''} for ${dt}`);
    } else if (okCount > 0) {
      setStatus('success');
      toast.warning(`Scheduled ${okCount} of ${selectedAccountIds.length}.`);
    } else {
      setStatus('error');
      const msg = results.map(r => r.error).filter(Boolean)[0] || 'Scheduling failed';
      setError(msg);
      toast.error(msg);
    }
  };

  const insertHook = (hook) => setCaption(prev => hook + '\n\n' + prev);
  const insertCTA = (cta) => setCaption(prev => prev + '\n\n' + cta);

  const handleSaveDraft = async () => {
    setStatus('scheduling');
    try {
      const res = await apiClient.post('/campaigns', {
        name: compositionName || 'Untitled Reel',
        type: contentType,
        content_type: contentType,
        caption,
        media_url: publicVideoUrl || '',
        brand_voice: brandVoice,
        property_data: propertyData || {},
        account_id: selectedAccountIds[0] || null,
        content_data: {},
      });
      if (res.data.campaign_id || res.data.success) {
        setStatus('success');
        toast.success('Saved as draft in My Campaigns');
        setTimeout(() => onClose(), 1200);
      }
    } catch (err) {
      setStatus('error');
      const msg = err.response?.data?.detail || 'Failed to save draft';
      setError(msg);
      toast.error(msg);
    }
  };

  if (!isOpen) return null;

  const igAccounts = accounts.filter(a => a.platform === 'instagram');
  const fbAccounts = accounts.filter(a => a.platform === 'facebook');
  const isProcessing = status === 'publishing' || status === 'scheduling';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={handleClose} data-testid="publish-dialog-overlay">
      <div className="bg-card border border-border/50 rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="publish-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/30">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-purple-400" />
            <span className="font-semibold text-sm">Publish Content</span>
          </div>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">&times;</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Property Data Summary */}
          {propertyData && (propertyData.address || propertyData.price) && (
            <div className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="publish-property-summary">
              <span className="font-semibold text-emerald-700 whitespace-nowrap">Property loaded</span>
              <span className="text-emerald-600 truncate">
                {[
                  propertyData.address,
                  propertyData.price,
                  propertyData.beds && `${propertyData.beds}bd`,
                  propertyData.baths && `${propertyData.baths}ba`,
                  propertyData.sqft && `${propertyData.sqft}sqft`,
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
          {/* Account Selector (multi-select — publish to IG + FB together) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Accounts <span className="text-muted-foreground/60">(select one or more)</span></label>
            {loadingAccounts ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading accounts...</div>
            ) : accounts.length === 0 ? (
              <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                No connected accounts. Go to Social Accounts to connect Instagram or Facebook.
              </div>
            ) : (
              <div className="border border-border/40 rounded-md divide-y divide-border/30" data-testid="publish-account-list">
                {igAccounts.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Instagram</div>}
                {igAccounts.map(a => {
                  const checked = selectedAccountIds.includes(a.account_id);
                  return (
                    <button
                      key={a.account_id}
                      type="button"
                      onClick={() => toggleAccount(a.account_id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${checked ? 'bg-purple-500/10' : 'hover:bg-muted/40'}`}
                      data-testid={`account-option-${a.account_id}`}
                      aria-pressed={checked}
                    >
                      <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-purple-500 border-purple-500' : 'border-border/60'}`}>
                        {checked && <CheckCircle className="h-3 w-3 text-white" />}
                      </span>
                      <Instagram className="h-3.5 w-3.5 text-pink-500 shrink-0" />
                      <span className="truncate">{a.account_name}</span>
                    </button>
                  );
                })}
                {fbAccounts.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Facebook</div>}
                {fbAccounts.map(a => {
                  const checked = selectedAccountIds.includes(a.account_id);
                  return (
                    <button
                      key={a.account_id}
                      type="button"
                      onClick={() => toggleAccount(a.account_id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${checked ? 'bg-purple-500/10' : 'hover:bg-muted/40'}`}
                      data-testid={`account-option-${a.account_id}`}
                      aria-pressed={checked}
                    >
                      <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-purple-500 border-purple-500' : 'border-border/60'}`}>
                        {checked && <CheckCircle className="h-3 w-3 text-white" />}
                      </span>
                      <Facebook className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                      <span className="truncate">{a.account_name}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedAccountIds.length > 0 && (
              <div className="text-[11px] text-muted-foreground" data-testid="selected-accounts-count">
                {selectedAccountIds.length} account{selectedAccountIds.length > 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {/* Content Type */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Content Type</label>
            <div className="flex gap-1.5" data-testid="publish-content-type">
              {Object.entries(CONTENT_TYPES).map(([id, ct]) => {
                const Icon = id === 'reel' ? Film : id === 'story' ? Circle : Image;
                return (
                  <button key={id} onClick={() => setContentType(id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md border text-xs font-medium transition-all ${
                      contentType === id ? 'border-purple-500/60 bg-purple-500/10 text-purple-400' : 'border-border/40 hover:border-border/60 text-muted-foreground'
                    }`} data-testid={`content-type-${id}`}>
                    <Icon className="h-3 w-3" /> {ct.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Brand Voice */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Brand Voice</label>
            <div className="flex gap-1.5" data-testid="publish-brand-voice">
              {Object.entries(BRAND_VOICES).map(([id, bv]) => (
                <button key={id} onClick={() => setBrandVoice(id)}
                  className={`flex-1 py-2 rounded-md border text-xs font-medium transition-all ${
                    brandVoice === id ? '' : 'border-border/40 hover:border-border/60 text-muted-foreground'
                  }`}
                  style={brandVoice === id ? { borderColor: bv.color + '80', backgroundColor: bv.color + '15', color: bv.color } : {}}
                  data-testid={`brand-voice-${id}`}>
                  {bv.name}
                </button>
              ))}
            </div>
          </div>

          {/* Caption */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Caption</label>
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={handleGenerateCaption} disabled={generatingCaption} data-testid="generate-caption-btn">
                {generatingCaption ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {generatingCaption ? 'Generating...' : propertyData?.address ? 'AI Generate from Property' : 'AI Generate'}
              </Button>
            </div>
            <Textarea
              value={caption} onChange={e => setCaption(e.target.value)}
              placeholder="Write your caption or click AI Generate..."
              rows={4} className="text-xs"
              maxLength={CONTENT_TYPES[contentType]?.maxLength || 2200}
              data-testid="publish-caption-input"
            />
            <span className="text-[10px] text-muted-foreground">{caption.length}/{CONTENT_TYPES[contentType]?.maxLength || 2200}</span>
          </div>

          {/* Quick Insert: Hooks & CTAs for Reels */}
          {contentType === 'reel' && (
            <div className="space-y-2">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground">Quick Hooks</label>
                <div className="flex gap-1 flex-wrap">
                  {REEL_HOOKS.slice(0, 5).map(h => (
                    <button key={h} onClick={() => insertHook(h)} className="px-2 py-0.5 text-[9px] rounded-full border border-border/40 text-muted-foreground hover:bg-purple-500/10 hover:text-purple-400 hover:border-purple-500/40 transition-colors">
                      {h}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground">Call to Action</label>
                <div className="flex gap-1 flex-wrap">
                  {CTA_PATTERNS.reel.map(c => (
                    <button key={c} onClick={() => insertCTA(c)} className="px-2 py-0.5 text-[9px] rounded-full border border-border/40 text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-400 hover:border-emerald-500/40 transition-colors">
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Publish Mode Toggle: Now / Schedule */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">When</label>
            <div className="flex gap-1.5" data-testid="publish-mode-toggle">
              <button onClick={() => setPublishMode('now')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md border text-xs font-medium transition-all ${
                  publishMode === 'now' ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-400' : 'border-border/40 hover:border-border/60 text-muted-foreground'
                }`} data-testid="publish-mode-now">
                <Send className="h-3 w-3" /> Publish Now
              </button>
              <button onClick={() => setPublishMode('schedule')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md border text-xs font-medium transition-all ${
                  publishMode === 'schedule' ? 'border-blue-500/60 bg-blue-500/10 text-blue-400' : 'border-border/40 hover:border-border/60 text-muted-foreground'
                }`} data-testid="publish-mode-schedule">
                <CalendarClock className="h-3 w-3" /> Schedule
              </button>
            </div>
          </div>

          {/* Schedule Date/Time Picker */}
          {publishMode === 'schedule' && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Schedule Date & Time</label>
              <Input
                type="datetime-local"
                value={scheduleDate}
                onChange={e => setScheduleDate(e.target.value)}
                min={getMinDateTime()}
                className="text-xs h-9"
                data-testid="schedule-datetime-input"
              />
              <span className="text-[10px] text-muted-foreground">
                Will publish at {scheduleDate ? new Date(scheduleDate).toLocaleString() : '—'}
              </span>
            </div>
          )}

          {/* Status Indicator */}
          {status !== 'idle' && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs ${
              status === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
              status === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
              'bg-blue-500/10 text-blue-400 border border-blue-500/20'
            }`} data-testid="publish-status">
              {status === 'success' ? <CheckCircle className="h-3.5 w-3.5" /> :
               status === 'error' ? <AlertCircle className="h-3.5 w-3.5" /> :
               <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span>{(isProcessing && progressMsg) ? progressMsg : STATUS_LABELS[status]}</span>
              {status === 'success' && publishMode === 'schedule' && <span className="text-[10px] ml-1">Scheduled!</span>}
              {error && <span className="text-[10px] ml-1 opacity-70">— {error}</span>}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1 h-9 text-xs gap-1.5" onClick={handleSaveDraft} disabled={isProcessing || status === 'success'} data-testid="save-draft-btn">
              <Save className="h-3 w-3" /> Save Draft
            </Button>
            {publishMode === 'now' ? (
              <Button
                className="flex-1 h-9 text-xs gap-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                onClick={handlePublishNow}
                disabled={selectedAccountIds.length === 0 || isProcessing || status === 'success'}
                data-testid="publish-submit-btn"
              >
                {isProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                {status === 'success' ? 'Published!' : 'Publish Now'}
              </Button>
            ) : (
              <Button
                className="flex-1 h-9 text-xs gap-1.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                onClick={handleSchedule}
                disabled={selectedAccountIds.length === 0 || isProcessing || status === 'success' || !scheduleDate}
                data-testid="schedule-submit-btn"
              >
                {isProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarClock className="h-3 w-3" />}
                {status === 'success' ? 'Scheduled!' : 'Schedule'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
